import { useId, useState } from 'react'
import { useStore } from '@tanstack/react-store'
import { Link } from '@tanstack/react-router'
import { Send } from 'lucide-react'
import { Skeleton } from 'skullet/react'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'

import type { Contact } from '@/gen/cortado/v1/contacts_pb'
import { EmailParticipantSchema, EmailThreadLiteSchema, type EmailThreadLite } from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'
import { formatShortDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { useContact } from '@/stores/contacts.store'
import { organizationStore } from '@/stores/organization.store'
import { Button } from '@/components/ui/button'
import { createEmailNameResolver, type EmailContactFallback } from '@/components/emails/email-addresses'
import { EmailComposeSheet } from '@/components/emails/email-compose-sheet'
import { EmailHistoryList } from '@/components/emails/email-history-list'
import { ConversationThreadDetail } from '@/components/emails/entity-conversations-panel'
import { inboxThreadBadge } from '@/components/emails/inbox/inbox-utils'
import { useEmailSenderOptions, type EmailSenderOption } from '@/components/emails/use-email-sender-options'

/**
 * The mailbox a new message from here sends from: the one the server names
 * to preselect (the caller's own, else the org default) when the caller may
 * send as it, else the org default if they may. Null when none is usable.
 */
export function pickSendingFromMailbox(
  options: readonly EmailSenderOption[],
  defaultMailboxId: string,
): EmailSenderOption | null {
  return (
    options.find((option) => option.id === defaultMailboxId && option.canSend) ??
    options.find((option) => option.isDefault && option.canSend) ??
    null
  )
}

/**
 * The contact that names a thread's unnamed addresses. The job's contact,
 * with its addresses, when the thread is that contact's (or names none);
 * otherwise only the thread's own contact name.
 */
export function threadContactFallback(
  thread: Pick<EmailThreadLite, 'contactId' | 'contactName'>,
  jobContact: Pick<Contact, 'id' | 'firstName' | 'lastName' | 'email' | 'emails'> | undefined,
): EmailContactFallback {
  if (jobContact && (!thread.contactId || thread.contactId === jobContact.id)) {
    const name = [jobContact.firstName, jobContact.lastName].map((part) => part?.trim()).filter(Boolean).join(' ')
    return {
      name: name || thread.contactName,
      emails: [jobContact.email ?? '', ...(jobContact.emails ?? []).map((entry) => entry.email)].filter(Boolean),
    }
  }
  return { name: thread.contactName }
}

/**
 * Who a thread is with, for its list row: one person by full name, several
 * by first name ("Cortado Pro, Elena" reads as "Cortado, Elena"), each
 * named through the shared resolver (participant name, then the contact)
 * and falling back to the address.
 */
export function threadParticipantsLabel(
  thread: Pick<EmailThreadLite, 'participants'>,
  contact?: EmailContactFallback,
): string {
  const nameFor = createEmailNameResolver(thread.participants, contact)
  const seen = new Set<string>()
  const people = thread.participants.filter((participant) => {
    const key = participant.email.trim().toLowerCase()
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
  const named = people.map((person) => ({ email: person.email, name: nameFor(person.email) }))
  if (named.length === 1) return named[0]!.name || named[0]!.email
  return named.map((person) => person.name.split(/\s+/)[0] || person.email).join(', ')
}

const BADGE_LABEL = { 'reply-needed': 'Reply needed', waiting: 'Waiting' } as const

interface JobThreadRowProps {
  organizationId: string
  inquiryId: string
  thread: EmailThreadLite
  /** Names the thread's addresses that carry no name of their own. */
  contact: EmailContactFallback
  isExpanded: boolean
  onToggle: () => void
}

/** One conversation: a row that opens the thread inline beneath it. */
function JobThreadRow({ organizationId, inquiryId, thread, contact, isExpanded, onToggle }: JobThreadRowProps) {
  const regionId = useId()
  const badge = inboxThreadBadge(thread)
  const participants = threadParticipantsLabel(thread, contact)

  return (
    <li>
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? regionId : undefined}
        onClick={onToggle}
        className={cn(
          'flex w-full cursor-pointer items-center gap-4 px-6 py-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
          isExpanded ? 'bg-muted' : 'hover:bg-muted/50',
          thread.isUnread && 'font-medium',
        )}
      >
        <span className="flex w-36 shrink-0 items-center gap-1.5 sm:w-44">
          {thread.isUnread ? (
            <span role="img" aria-label="Unread" className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
          ) : (
            <span aria-hidden className="w-1.5 shrink-0" />
          )}
          <span className="truncate">{participants || 'Unknown'}</span>
          {thread.messageCount > 1 && (
            <span className="shrink-0 text-xs font-normal text-muted-foreground tabular-nums">{thread.messageCount}</span>
          )}
        </span>
        <span className="min-w-0 flex-1 truncate">
          {thread.subject || '(no subject)'}
          {thread.lastMessagePreview && (
            <span className="font-normal text-muted-foreground"> — {thread.lastMessagePreview}</span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-2 text-xs font-normal text-muted-foreground">
          {badge && <span className="hidden sm:inline">{BADGE_LABEL[badge]}</span>}
          {thread.lastMessageAt && <time className="tabular-nums">{formatShortDate(thread.lastMessageAt)}</time>}
        </span>
      </button>
      {isExpanded && (
        <div id={regionId} role="region" aria-label={thread.subject || '(no subject)'} className="border-t px-6 py-4">
          <ConversationThreadDetail
            organizationId={organizationId}
            threadId={thread.id}
            replyParent={{ parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY, parentId: inquiryId }}
            hideLinks
            contact={contact}
          />
        </div>
      )}
    </li>
  )
}

const SAMPLE_THREADS: EmailThreadLite[] = [
  { subject: 'Re: Menu options for the reception', preview: 'Thanks, the second option looks perfect for us.' },
  { subject: 'Quote for your June event', preview: 'Here is the quote we discussed on our call.' },
  { subject: 'Final headcount', preview: 'We are now at 120 guests, including 8 children.' },
].map(({ subject, preview }, i) =>
  create(EmailThreadLiteSchema, {
    id: `sample-${i}`,
    subject,
    lastMessagePreview: preview,
    lastMessageAt: timestampFromDate(new Date(2026, 0, i + 1)),
    messageCount: 2,
    participants: [create(EmailParticipantSchema, { name: 'Jordan Rivera', email: 'jordan@example.com', type: 'contact' })],
  }),
)

interface JobActivityEmailsProps {
  jobId: string
  inquiryId?: string
  /** The job's client, whose address seeds Compose's To. */
  contactId?: string
  threads: EmailThreadLite[]
  totalCount: number
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

/**
 * The mailbox a new message from the job sends from, shared by the Emails
 * tab's "Sending from" line and the card header's Compose (one cached query).
 * Only queried with the Connected inbox add-on and a linked inquiry.
 */
function useJobSendingFrom(inquiryId: string | undefined) {
  const organizationId = useStore(organizationStore, (state) => state.selectedOrganizationId) ?? ''
  const { hasFeature: hasEmailInbox } = useHasEmailInboxFeature()
  const senders = useEmailSenderOptions({ organizationId, open: hasEmailInbox && Boolean(inquiryId) })
  const sendingFrom = pickSendingFromMailbox(senders.senderOptions, senders.defaultAccountId)
  const hasNoMailbox = !senders.isLoading && !senders.isError && !sendingFrom
  return {
    organizationId,
    sendingFrom,
    hasNoMailbox,
    canCompose: Boolean(organizationId) && !senders.isLoading && !hasNoMailbox,
  }
}

/**
 * The Activity card header's Compose: a new message on the job's inquiry,
 * To the job's client. Hidden without a linked inquiry or the Connected inbox
 * add-on (the legacy history is read-only); disabled with no usable mailbox.
 */
export function JobComposeButton({ inquiryId, contactId }: { inquiryId?: string; contactId?: string }) {
  const { hasFeature: hasEmailInbox } = useHasEmailInboxFeature()
  const [isComposeOpen, setIsComposeOpen] = useState(false)
  const { organizationId, canCompose } = useJobSendingFrom(inquiryId)
  const { contact } = useContact(hasEmailInbox && inquiryId ? contactId || undefined : undefined)

  if (!inquiryId || !hasEmailInbox) return null
  return (
    <>
      {/* -my-1: overhangs the title line like the tab bar beside it. */}
      <Button type="button" size="sm" className="-my-1" onClick={() => setIsComposeOpen(true)} disabled={!canCompose}>
        <Send className="h-4 w-4" />
        Compose
      </Button>
      {canCompose && (
        <EmailComposeSheet
          mode="compose"
          organizationId={organizationId}
          parentType={ParentTypeEnum.PARENT_TYPE_INQUIRY}
          parentId={inquiryId}
          inquiryId={inquiryId}
          contactId={contactId || undefined}
          toEmail={contact?.email ?? ''}
          templateContext="inquiry"
          open={isComposeOpen}
          onOpenChange={setIsComposeOpen}
        />
      )}
    </>
  )
}

/**
 * The Activity card's Emails tab: the job's conversations (its inquiry's,
 * rolled up with its quotes, booking and invoices) as an inline threaded
 * view, under the mailbox a new message sends from (Compose sits in the card
 * header, `JobComposeButton`). One thread is open at a time; opening it marks
 * it read and offers Reply.
 *
 * Orgs without the Connected inbox (EmailInbox) add-on get the legacy
 * read-only email history instead, as record pages do.
 */
export function JobActivityEmails({
  jobId,
  inquiryId,
  contactId,
  threads,
  totalCount,
  isLoading,
  isError,
  onRetry,
}: JobActivityEmailsProps) {
  const { hasFeature: hasEmailInbox, hasLoaded: hasLoadedFeatures } = useHasEmailInboxFeature()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const { organizationId, sendingFrom, hasNoMailbox } = useJobSendingFrom(inquiryId)
  const { contact } = useContact(hasEmailInbox && inquiryId ? contactId || undefined : undefined)

  if (!inquiryId) {
    return <p className="px-6 py-4 text-sm text-muted-foreground">No emails yet.</p>
  }

  const threadRows = (rows: EmailThreadLite[]) => (
    <ul className="divide-y">
      {rows.map((thread) => (
        <JobThreadRow
          key={thread.id}
          organizationId={organizationId}
          inquiryId={inquiryId}
          thread={thread}
          contact={threadContactFallback(thread, contact)}
          isExpanded={expandedId === thread.id}
          onToggle={() => setExpandedId((current) => (current === thread.id ? null : thread.id))}
        />
      ))}
    </ul>
  )

  if (!hasEmailInbox) {
    if (!hasLoadedFeatures) {
      return (
        <Skeleton data={null} sample={SAMPLE_THREADS} label="Loading emails">
          {threadRows}
        </Skeleton>
      )
    }
    return (
      <div className="px-6 py-4">
        <EmailHistoryList
          organizationId={organizationId}
          parentType={ParentTypeEnum.PARENT_TYPE_INQUIRY}
          parentId={inquiryId}
        />
      </div>
    )
  }

  const body =
    isError && !isLoading ? (
      <div role="alert" className="flex items-center justify-between gap-3 px-6 py-4 text-sm">
        <span className="text-muted-foreground">Couldn't load this job's emails.</span>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    ) : (
      <Skeleton
        data={isLoading ? null : threads}
        sample={SAMPLE_THREADS}
        label="Loading emails"
        empty={!isLoading && threads.length === 0}
        emptyMessage={<p className="px-6 py-4 text-sm text-muted-foreground">No emails on this job yet</p>}
      >
        {(threads) => (
          <>
            {threadRows(threads)}
            {totalCount > threads.length && (
              <div className="border-t px-6 py-3 text-xs">
                <Link
                  to="/pipeline/$id/leads/$leadId"
                  params={{ id: jobId, leadId: inquiryId }}
                  search={{ tab: 'conversations' }}
                  className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  View all {totalCount} on the lead
                </Link>
              </div>
            )}
          </>
        )}
      </Skeleton>
    )

  return (
    <div>
      <div className="border-b px-6 py-3">
        <p className="min-w-0 truncate text-sm text-muted-foreground">
          {sendingFrom ? (
            <>Sending from {sendingFrom.emailAddress}</>
          ) : hasNoMailbox ? (
            <Link
              to="/email"
              search={{ section: 'connected-inbox' }}
              className="underline-offset-4 hover:text-foreground hover:underline"
            >
              Connect your inbox to email from here
            </Link>
          ) : null}
        </p>
      </div>
      {body}
    </div>
  )
}
